import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  useTheme,
  LinearProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddPaymentMethodModal from "../../components/buyer/ProfileOverview/AddPaymentMethodModal";

// ── Small inline icons ──────────────────────────────────────────────────────
const ArrowRight = ({ color = "currentColor", size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path
      d="M3 8h10M9 4l4 4-4 4"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ArrowLeft = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path
      d="M13 8H3M7 4l-4 4 4 4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 3v10M3 8h10"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

// ── Action row inside a card ─────────────────────────────────────────────────
const ActionItem = ({
  iconBg,
  icon,
  label,
  sublabel,
  onClick,
  divider,
  theme,
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: { xs: 2.5, md: 3.5 },
      py: 2,
      gap: 2,
      borderBottom: divider ? `1px solid ${theme.palette.divider}` : "none",
      cursor: "pointer",
      transition: "background .12s",
      "&:hover": { backgroundColor: theme.palette.grey[50] },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          backgroundColor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: 13.5,
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {label}
        </Typography>
        {sublabel && (
          <Typography
            sx={{ fontSize: 12, color: theme.palette.text.disabled, mt: 0.25 }}
          >
            {sublabel}
          </Typography>
        )}
      </Box>
    </Box>
    <Box sx={{ color: theme.palette.text.disabled, flexShrink: 0 }}>
      <ArrowRight />
    </Box>
  </Box>
);

// ── Payment method row ───────────────────────────────────────────────────────
const PaymentMethodRow = ({
  name,
  expiry,
  isDefault,
  onEdit,
  onRemove,
  onSetDefault,
  theme,
  divider,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      py: 1.75,
      borderBottom: divider ? `1px solid ${theme.palette.divider}` : "none",
      flexWrap: "wrap",
    }}
  >
    {/* Card chip icon */}
    <Box
      sx={{
        width: 42,
        height: 28,
        borderRadius: "6px",
        backgroundColor: "#1a1a2e",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <svg width="26" height="16" viewBox="0 0 38 24" fill="none">
        <rect width="38" height="24" rx="4" fill="#1a1a2e" />
        <rect x="2" y="8" width="34" height="4" fill="rgba(255,255,255,0.15)" />
      </svg>
    </Box>

    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 13.5,
          fontWeight: 600,
          color: theme.palette.text.primary,
        }}
      >
        {name}
      </Typography>
      <Typography sx={{ fontSize: 12, color: theme.palette.text.disabled }}>
        {expiry}
      </Typography>
    </Box>

    {isDefault && (
      <Box
        sx={{
          px: 1,
          py: 0.4,
          borderRadius: "6px",
          backgroundColor: "#FFF8DC",
          fontSize: 11,
          fontWeight: 700,
          color: "#b45309",
          flexShrink: 0,
        }}
      >
        Default
      </Box>
    )}

    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
      {!isDefault && (
        <Button
          size="small"
          onClick={onSetDefault}
          sx={{
            fontSize: 12,
            fontWeight: 600,
            textTransform: "none",
            border: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.secondary,
            borderRadius: "7px",
            px: 1.5,
            py: 0.6,
            "&:hover": { borderColor: "#F5C518", color: "#111" },
          }}
        >
          Set Default
        </Button>
      )}
      <Button
        size="small"
        onClick={onEdit}
        sx={{
          fontSize: 12,
          fontWeight: 600,
          textTransform: "none",
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.secondary,
          borderRadius: "7px",
          px: 1.5,
          py: 0.6,
          "&:hover": { borderColor: "#F5C518", color: "#111" },
        }}
      >
        Edit
      </Button>
      <Button
        size="small"
        onClick={onRemove}
        sx={{
          fontSize: 12,
          fontWeight: 600,
          textTransform: "none",
          border: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.secondary,
          borderRadius: "7px",
          px: 1.5,
          py: 0.6,
          "&:hover": { borderColor: "#fca5a5", color: "#dc2626" },
        }}
      >
        Remove
      </Button>
    </Box>
  </Box>
);

// ── Main page ────────────────────────────────────────────────────────────────
const Billing = ({ user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);

  const yellow = theme.palette.scraptradeYellow?.main || "#F5C518";
  const yellowDark = theme.palette.scraptradeYellow?.dark || "#d4a800";

  const plan = user?.plan || "pro";
  const status = user?.subscription_status || "active";
  const renewalDate = user?.renewal_date || "12 March 2026";
  const daysLeft = user?.days_left || 10;
  const totalDays = 30;
  const progressValue = Math.max(
    0,
    Math.min(100, ((totalDays - daysLeft) / totalDays) * 100),
  );
  const isFree = plan === "free";

  const paymentMethods = user?.payment_methods || [
    { id: 1, name: "Visa •••• 4242", expiry: "Expires 08/27", isDefault: true },
    {
      id: 2,
      name: "Amex •••• 1008",
      expiry: "Expires 03/26",
      isDefault: false,
    },
  ];

  const cardSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "18px",
    overflow: "hidden",
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
    mb: 2,
  };

  const sectionHeaderSx = {
    px: { xs: 2.5, md: 3.5 },
    py: 2.25,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  const handleSaveCard = (cardData) => {
    console.log("New card:", cardData);
    // call your API to save the card
  };

  return (
    <>
      <Box sx={{ maxWidth: 720, mx: "auto", px: { xs: 2, md: 3 }, py: 4 }}>
        {/* Back */}
        <Button
          disableRipple
          onClick={() => navigate(-1)}
          startIcon={<ArrowLeft />}
          sx={{
            mb: 3,
            p: 0,
            background: "none",
            color: theme.palette.text.secondary,
            fontSize: 13,
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              background: "none",
              color: theme.palette.text.primary,
            },
          }}
        >
          Back to Account
        </Button>

        {/* Page heading */}
        <Typography
          sx={{
            fontSize: 22,
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 0.5,
          }}
        >
          Subscription & Billing
        </Typography>
        <Typography
          sx={{ fontSize: 13, color: theme.palette.text.secondary, mb: 3.5 }}
        >
          Manage your plan, payment methods, and invoices.
        </Typography>

        {/* ── Current Plan card ── */}
        <Paper elevation={0} sx={cardSx}>
          <Box sx={sectionHeaderSx}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              Current Plan
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 2.5, md: 3.5 }, py: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    lineHeight: 1.2,
                  }}
                >
                  {isFree ? "Free Plan" : "Pro Seller"}
                </Typography>
                {!isFree && (
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: theme.palette.text.secondary,
                      mt: 0.5,
                    }}
                  >
                    £9.99 / month
                  </Typography>
                )}
              </Box>
              <Chip
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: isFree ? "#9ca3af" : "#16a34a",
                        opacity: 0.8,
                      }}
                    />
                    {isFree
                      ? "Free"
                      : status.charAt(0).toUpperCase() + status.slice(1)}
                  </Box>
                }
                size="small"
                sx={{
                  backgroundColor: isFree ? theme.palette.grey[100] : "#f0fdf4",
                  color: isFree ? theme.palette.text.secondary : "#16a34a",
                  border: isFree ? "none" : "1px solid #bbf7d0",
                  fontWeight: 600,
                  fontSize: 12,
                  height: 28,
                  "& .MuiChip-label": { px: 1.25 },
                }}
              />
            </Box>

            {!isFree && (
              <Box sx={{ mt: 2.5 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: theme.palette.text.disabled,
                      fontWeight: 500,
                    }}
                  >
                    Billing cycle
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: theme.palette.text.secondary,
                      fontWeight: 500,
                    }}
                  >
                    {daysLeft} days left · Renews {renewalDate}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressValue}
                  sx={{
                    height: 4,
                    borderRadius: 99,
                    backgroundColor: theme.palette.grey[100],
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 99,
                      backgroundColor: daysLeft <= 5 ? "#f59e0b" : yellow,
                    },
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Actions */}
          <ActionItem
            theme={theme}
            iconBg="#FFF8DC"
            icon={
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 3l1.5 4.5H18l-3.75 2.75 1.5 4.75L12 12l-3.75 3 1.5-4.75L6 7.5h4.5L12 3z"
                  stroke="#d4a800"
                  strokeWidth="1.6"
                  strokeLinejoin="round"
                />
              </svg>
            }
            label="Change Plan"
            sublabel="Switch to a different subscription tier"
            onClick={() => navigate("/pricing")}
            divider={true}
          />
          <ActionItem
            theme={theme}
            iconBg="#f0fdf4"
            icon={
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 3v9m0 0l-3-3m3 3l3-3M4 17h16v2a1 1 0 01-1 1H5a1 1 0 01-1-1v-2z"
                  stroke="#16a34a"
                  strokeWidth="1.7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
            label="Download Invoices"
            sublabel="View and download past invoices"
            onClick={() => navigate("/settings/billing/history")}
            divider={false}
          />
        </Paper>

        {/* ── Payment Methods card ── */}
        <Paper elevation={0} sx={cardSx}>
          <Box sx={sectionHeaderSx}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              Payment Methods
            </Typography>
          </Box>

          <Box sx={{ px: { xs: 2.5, md: 3.5 }, py: 2 }}>
            {paymentMethods.map((pm, i) => (
              <PaymentMethodRow
                key={pm.id}
                theme={theme}
                name={pm.name}
                expiry={pm.expiry}
                isDefault={pm.isDefault}
                divider={i < paymentMethods.length - 1}
                onEdit={() => {}}
                onRemove={() => {}}
                onSetDefault={() => {}}
              />
            ))}

            {/* Add payment method */}
            <Button
              fullWidth
              disableRipple
              startIcon={<PlusIcon />}
              sx={{
                mt: 2,
                py: 1.25,
                border: `1.5px dashed ${theme.palette.divider}`,
                borderRadius: "10px",
                backgroundColor: "transparent",
                color: theme.palette.text.disabled,
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "transparent",
                  borderColor: yellow,
                  color: theme.palette.text.primary,
                },
              }}
              onClick={() => setModalOpen(true)}
            >
              Add Payment Method
            </Button>
          </Box>
        </Paper>

        {/* ── Cancel Subscription card ── */}
        <Paper elevation={0} sx={{ ...cardSx, mb: 0 }}>
          <Box sx={sectionHeaderSx}>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: theme.palette.text.primary,
              }}
            >
              Cancel Subscription
            </Typography>
          </Box>
          <Box
            sx={{
              px: { xs: 2.5, md: 3.5 },
              py: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                color: theme.palette.text.secondary,
                maxWidth: 380,
                lineHeight: 1.6,
              }}
            >
              Cancelling will downgrade your account to Free at the end of your
              current billing period. Your listings will be removed.
            </Typography>
            <Button
              sx={{
                flexShrink: 0,
                px: 2.25,
                py: 1,
                border: "1.5px solid #fca5a5",
                borderRadius: "9px",
                color: "#dc2626",
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#fef2f2",
                  borderColor: "#f87171",
                },
              }}
            >
              Cancel Subscription
            </Button>
          </Box>
        </Paper>
      </Box>

      <AddPaymentMethodModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSaveCard}
      />
    </>
  );
};

export default Billing;
